import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, createRef, useRef, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { b as HOST_PLATFORM, c as LINK_TYPES, A as ARIA_LABELS, S as SKELETON_VARIANTS, d as SOURCE_PREFIX, M as MKT_CONTAINER_ID, C as CRITERIA_TYPE, F as FOCUS_OUTLINE_CLASS, O as OFFER_CTA_PREFIX } from "./index-BIblv54e.js";
import { l as logoLarge, a as logoSmall } from "./logo-quickenloans-small-Gce_Lb86.js";
import { G as GlobalHeadContext, g as getEnvVariable } from "./index-C9TrZnbr.js";
import { styled as styled$1 } from "@mui/system";
import { IconButton, Skeleton as Skeleton$1, Box, InputAdornment, InputLabel, FormHelperText, OutlinedInput, List, ListItem, ListItemIcon } from "@mui/material";
import { Search, ChevronRight, Close } from "@mui/icons-material";
import { BREAKPOINT_DIMENSIONS } from "@quickenloans-ui-suite/design-system";
import { s as slugify, a as addQueryParams } from "./addQueryParams-CbXuSGTY.js";
import { u as useSourceIdCookie } from "./useSourceIdCookie-DNjWFuC4.js";
import { BYOButton } from "@cdm/quickenloans-library/lib/";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
function getDefaultExportFromCjs(x) {
  return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, "default") ? x["default"] : x;
}
var propTypes = { exports: {} };
var ReactPropTypesSecret$1 = "SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED";
var ReactPropTypesSecret_1 = ReactPropTypesSecret$1;
var ReactPropTypesSecret = ReactPropTypesSecret_1;
function emptyFunction() {
}
function emptyFunctionWithReset() {
}
emptyFunctionWithReset.resetWarningCache = emptyFunction;
var factoryWithThrowingShims = function() {
  function shim(props, propName, componentName, location, propFullName, secret) {
    if (secret === ReactPropTypesSecret) {
      return;
    }
    var err = new Error(
      "Calling PropTypes validators directly is not supported by the `prop-types` package. Use PropTypes.checkPropTypes() to call them. Read more at http://fb.me/use-check-prop-types"
    );
    err.name = "Invariant Violation";
    throw err;
  }
  shim.isRequired = shim;
  function getShim() {
    return shim;
  }
  var ReactPropTypes = {
    array: shim,
    bigint: shim,
    bool: shim,
    func: shim,
    number: shim,
    object: shim,
    string: shim,
    symbol: shim,
    any: shim,
    arrayOf: getShim,
    element: shim,
    elementType: shim,
    instanceOf: getShim,
    node: shim,
    objectOf: getShim,
    oneOf: getShim,
    oneOfType: getShim,
    shape: getShim,
    exact: getShim,
    checkPropTypes: emptyFunctionWithReset,
    resetWarningCache: emptyFunction
  };
  ReactPropTypes.PropTypes = ReactPropTypes;
  return ReactPropTypes;
};
{
  propTypes.exports = factoryWithThrowingShims();
}
var propTypesExports = propTypes.exports;
const PropTypes = /* @__PURE__ */ getDefaultExportFromCjs(propTypesExports);
const LogoImage$1 = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  "&:hover": {
    cursor: "pointer"
  }
});
const convertHrefToUrl = (href, baseUrl) => {
  try {
    return new URL(href).href;
  } catch (e) {
    return new URL(href, baseUrl).href;
  }
};
const isOffSite = (hostname, href, target) => {
  var _a;
  if (!hostname) return true;
  if (target === "_blank") return true;
  const url = convertHrefToUrl(href, ((_a = window == null ? void 0 : window.location) == null ? void 0 : _a.href) || `https://${hostname}`);
  return !url.includes(hostname);
};
const getLocalPath = (href, property = null) => {
  const url = new URL(href, "https://www.quickenloans.com");
  if (property) return url[property];
  return `${url.pathname}${url.search}${url.hash}`;
};
const LinkResolver = forwardRef(({
  href,
  target = "_self",
  ...props
}, ref) => {
  const { LinkComponent: Link, uat, hostname, hostPlatform } = useContext(GlobalHeadContext);
  const linkType = hostPlatform === HOST_PLATFORM.GATSBY ? LINK_TYPES.GATSBY : LINK_TYPES.NEXT;
  const linkAttribute = {
    [linkType]: href
  };
  const linkProps = {
    ...props
  };
  if (!href || href === "#") {
    return /* @__PURE__ */ jsx("button", { ...linkProps });
  }
  linkProps.itemProp = "url";
  const uatHref = hostPlatform === HOST_PLATFORM.GATSBY ? "/eval-target" : href;
  const anchorHref = uat ? uatHref : href;
  const localPath = getLocalPath(anchorHref);
  linkAttribute[linkType] = uat ? uatHref : localPath;
  const isSameHostname = uat || hostname === getLocalPath(href, "hostname");
  let isSamePathname = false;
  if (typeof window !== "undefined") {
    isSamePathname = uat ? getLocalPath(window.location.href).toLowerCase().startsWith(localPath.toLowerCase()) : getLocalPath(window.location.href).toLowerCase() === localPath.toLowerCase();
  }
  const isOffSiteHref = uat ? false : isOffSite(hostname, anchorHref, target);
  let shouldRenderAnchor = Link === "a";
  if (isSameHostname && isSamePathname) shouldRenderAnchor = true;
  if (isOffSiteHref) shouldRenderAnchor = true;
  if (shouldRenderAnchor) {
    return /* @__PURE__ */ jsx("a", { ref, href: anchorHref, target, ...linkProps });
  }
  linkProps["data-type"] = "hostlink";
  return /* @__PURE__ */ jsx(Link, { ref, ...linkAttribute, ...linkProps, "data-name": "host-link" });
});
LinkResolver.propTypes = {
  /** The URL to navigate to */
  href: PropTypes.string,
  /** The target attribute for the link */
  target: PropTypes.string
};
function Logo({ showLarge, sx = {}, testId = "global-header-logo" }) {
  const { environment: currentEnv } = useContext(GlobalHeadContext);
  const logoSrc = showLarge ? logoLarge : logoSmall;
  const href = getEnvVariable({
    currentEnv,
    keyToSearch: "APP_BASE_URL"
  });
  return /* @__PURE__ */ jsx(LinkResolver, { id: "global-header-logo", href, children: /* @__PURE__ */ jsx(
    LogoImage$1,
    {
      src: logoSrc,
      alt: "Quicken Loans logo",
      title: "Quicken Loans logo",
      role: "img",
      "aria-label": ARIA_LABELS.LOGO,
      sx,
      "data-testid": testId
    }
  ) });
}
Logo.propTypes = {
  showLarge: PropTypes.bool,
  sx: PropTypes.object,
  testId: PropTypes.string
};
const StyledIconButton = ({ isActive, children, ...rest }) => /* @__PURE__ */ jsx(IconButton, { ...rest, children });
const StyledButton = styled$1(StyledIconButton)(({ theme, isActive }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  return {
    padding: 8,
    color: isActive ? (_a = theme.vars.palette.grey) == null ? void 0 : _a[80] : (_b = theme.vars.palette.grey) == null ? void 0 : _b[70],
    background: isActive ? (_c = theme.vars.palette.grey) == null ? void 0 : _c[10] : (_d = theme.vars.palette.grey) == null ? void 0 : _d[0],
    "&:hover": {
      color: (_e = theme.vars.palette.grey) == null ? void 0 : _e[80],
      background: (_f = theme.vars.palette.grey) == null ? void 0 : _f[5]
    },
    "&:active": {
      color: (_g = theme.vars.palette.grey) == null ? void 0 : _g[90],
      background: (_h = theme.vars.palette.grey) == null ? void 0 : _h[10]
    },
    "&:focus-visible": {
      outline: "auto 1px"
    }
  };
});
const SearchIcon = ({
  onClick = () => {
  },
  isActive = false
}) => {
  return /* @__PURE__ */ jsx(
    StyledButton,
    {
      size: "small",
      id: "SearchIconButton",
      "data-testid": "SearchIconButton",
      disableRipple: true,
      onClick,
      "aria-label": ARIA_LABELS.SEARCH_ICON,
      role: "button",
      isActive,
      children: /* @__PURE__ */ jsx(Search, {})
    }
  );
};
SearchIcon.propTypes = {
  onClick: PropTypes.func,
  isActive: PropTypes.bool
};
const getFlexAlignment = (hasChildren, hasIcon) => ({
  justifyContent: hasIcon && hasChildren ? "space-between" : "center",
  alignItems: "center"
});
const getPadding = (hasChildren, hasIcon) => {
  const paddingMap = {
    "true-true": "6px 4px 6px 8px",
    "true-false": "8px 12px 8px 8px",
    "false-true": "8px 4px 8px 8px",
    "false-false": "8px"
  };
  const key = `${hasIcon}-${hasChildren}`;
  return paddingMap[key];
};
const getActiveStyles = (isActive) => isActive ? {
  color: "var(--color-primary-110)",
  background: "var(--color-primary-005)"
} : {};
const IconWrapper = styled$1("div")({
  display: "inline-flex",
  justifyContent: "flex-end",
  alignItems: "center",
  flexGrow: 1
});
const IconSpan = styled$1("div")({
  display: "inline-flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "20px",
  height: "20px",
  svg: {
    width: "100%",
    height: "100%"
  }
});
const Container = styled$1("div")(({
  theme,
  hasChildren = false,
  hasIcon = false,
  isActive = false,
  isForMainNav = false
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const { link } = theme["global-head"].nav.desktop;
  return {
    ...getFlexAlignment(hasChildren, hasIcon),
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: `${isForMainNav ? "0" : `${link.borderRadius}`}px`,
    boxSizing: "border-box",
    display: "inline-flex",
    flexShrink: 0,
    gap: `${link.gap}px`,
    height: `${link.height}px`,
    maxHeight: `${link.height}px`,
    justifyContent: "flex-start",
    outline: "none",
    padding: getPadding(hasChildren, hasIcon),
    textDecoration: "none",
    width: "100%",
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
    /* Remove the ripple effect on mobile devices */
    ...isForMainNav ? {
      borderBottom: "2px solid transparent",
      transition: "border-bottom 0.3s ease, background-color 0.3s ease",
      "&:hover": {
        borderBottom: `2px solid ${(_a = theme.vars.palette.primary) == null ? void 0 : _a[100]}`,
        ...isActive ? {
          span: {
            color: (_b = theme.vars.palette.primary) == null ? void 0 : _b[100]
          }
        } : {}
      },
      ...isActive ? {
        borderBottom: `2px solid ${(_c = theme.vars.palette.primary) == null ? void 0 : _c[100]}`,
        span: {
          color: (_d = theme.vars.palette.primary) == null ? void 0 : _d[100]
        }
      } : {}
    } : {
      "@media (hover: hover)": {
        // allow hover styles on devices that support it
        "&:hover": {
          background: (_e = theme.vars.palette.primary) == null ? void 0 : _e[5]
        }
      },
      "&:active": {
        // preserve style when clicking on touch devices
        background: (_f = theme.vars.palette.primary) == null ? void 0 : _f[5]
      }
    },
    svg: {
      fill: isActive ? (_g = theme.vars.palette.primary) == null ? void 0 : _g[110] : (_h = theme.vars.palette.grey) == null ? void 0 : _h[80]
    },
    [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_large)]: {
      width: "auto"
    },
    ...getActiveStyles(isActive && !isForMainNav)
  };
});
const LinkWrapper = styled$1(forwardRef(({
  hasChildren,
  hasIcon,
  isActive,
  isForMainNav,
  theme,
  ...rest
}, ref) => /* @__PURE__ */ jsx(LinkResolver, { ref, ...rest })))(({ isActive = false, theme = {} }) => {
  var _a, _b;
  return {
    boxShadow: "none",
    color: isActive ? (_a = theme.vars.palette.primary) == null ? void 0 : _a[110] : (_b = theme.vars.palette.grey) == null ? void 0 : _b[80],
    fontFamily: theme.typography.fontFamily,
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: 0,
    textDecoration: "none",
    backgroundColor: "transparent",
    borderWidth: 0,
    fontSize: theme.typography.htmlFontSize,
    fontStyle: "normal",
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: theme.customSpacing.lineHeight,
    cursor: "pointer",
    span: {
      maxHeight: "37px",
      overflow: "hidden",
      textAlign: "left"
    }
  };
});
const LinkIcon = ({ icon, testId }) => /* @__PURE__ */ jsx(IconWrapper, { "data-testid": testId, children: /* @__PURE__ */ jsx(IconSpan, { children: icon }) });
const LinkTitle = ({ testId, title, ...rest }) => /* @__PURE__ */ jsx("span", { "data-testid": testId, ...rest, children: title });
const NavigationLinkContainer = ({ className, icon, onClick, sx, title, linkWrapperRef, ...rest }) => {
  var _a;
  const defaultTestId = slugify(`n-link-${title}`);
  const titleItemProp = ((_a = rest == null ? void 0 : rest.href) == null ? void 0 : _a.length) && rest.href !== "#" ? "name" : null;
  return /* @__PURE__ */ jsxs(
    Container,
    {
      className,
      "data-testid": `${defaultTestId}-container`,
      onClick,
      sx,
      ...rest,
      children: [
        /* @__PURE__ */ jsxs(
          LinkWrapper,
          {
            ref: linkWrapperRef,
            "aria-selected": (rest == null ? void 0 : rest.isActive) ? "true" : "false",
            "data-testid": `${defaultTestId}-wrapper`,
            id: `${defaultTestId}-wrapper-${rest.isForMainNav ? "main" : "sub"}`,
            ...rest,
            children: [
              (rest == null ? void 0 : rest.hasIcon) && /* @__PURE__ */ jsx(LinkIcon, { icon, testId: `${defaultTestId}-icon-left` }),
              /* @__PURE__ */ jsx(LinkTitle, { testId: `${defaultTestId}-title`, title, itemProp: titleItemProp })
            ]
          }
        ),
        (rest == null ? void 0 : rest.hasChildren) && /* @__PURE__ */ jsx(LinkIcon, { icon: /* @__PURE__ */ jsx(ChevronRight, {}), testId: `${defaultTestId}-icon-right` })
      ]
    }
  );
};
const NavigationLink = ({
  className = "",
  hasChildren = false,
  href,
  target,
  icon,
  isActive = false,
  isForMainNav = true,
  onClick = () => {
  },
  title,
  sx = {}
}) => {
  const linkWrapperRef = createRef();
  const hasIcon = Boolean(icon);
  const handleClick = (event) => {
    if (linkWrapperRef.current) {
      linkWrapperRef.current.click();
      return;
    }
    onClick(event);
  };
  return /* @__PURE__ */ jsx(
    NavigationLinkContainer,
    {
      className,
      hasChildren,
      hasIcon,
      href,
      target,
      icon,
      isActive,
      isForMainNav,
      onClick: handleClick,
      sx,
      title,
      linkWrapperRef
    }
  );
};
NavigationLink.propTypes = {
  /** Static className that will allow overriding CSS module styling */
  className: PropTypes.string,
  /** When set to true it add the chevron right icon */
  hasChildren: PropTypes.bool,
  /** The URL to navigate to. If not provided, the NavigationLink will render as a button */
  href: PropTypes.string,
  /** The target attribute for the link */
  target: PropTypes.string,
  /** Inline SVG or a component that renders to an inline SVG. Will either appear left of text or in the center if there is not text provided. */
  icon: PropTypes.element,
  /** When set to true the background and font color will be changed */
  isActive: PropTypes.bool,
  /** When set to true the border radius will be removed */
  isForMainNav: PropTypes.bool,
  onClick: PropTypes.func,
  /** The system prop that allows defining system overrides as well as additional CSS styles */
  sx: PropTypes.object,
  /** NavigationLink title */
  title: PropTypes.string.isRequired
};
const HamburgerIconStyled = styled$1("svg")({
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  "&.open": {
    "& .top": {
      transform: "translate(0px, 5px) rotate(45deg)"
    },
    "& .middle": {
      transform: "scaleX(0)",
      opacity: 0
    },
    "& .bottom": {
      transform: "translate(0px, -5px) rotate(-45deg)"
    }
  }
});
const RectStyled = styled$1("rect")(({ theme }) => {
  var _a;
  return {
    transformOrigin: "center",
    transformBox: "fill-box",
    width: 20,
    height: 2,
    x: 2,
    fill: (_a = theme.vars.palette.grey) == null ? void 0 : _a[80],
    transition: "all 0.3s ease-in-out"
  };
});
const defaultStyles = {
  padding: 0,
  "&:focus-visible": {
    outline: "auto 1px black"
  }
};
const HamburgerButton = ({
  sx = {},
  isOpen = false,
  onClick = () => {
  },
  testId = "hamburger-button"
}) => {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      sx: { ...defaultStyles, ...sx },
      size: "small",
      id: "hamburger-button",
      "data-testid": testId,
      disableRipple: true,
      onClick,
      "aria-label": ARIA_LABELS.NAVIGATION_MENU,
      "aria-expanded": isOpen,
      role: "button",
      children: /* @__PURE__ */ jsxs(
        HamburgerIconStyled,
        {
          "data-testid": `${testId}-icon`,
          className: isOpen ? "open" : "",
          children: [
            /* @__PURE__ */ jsx(RectStyled, { y: "6.5", className: "top" }),
            /* @__PURE__ */ jsx(RectStyled, { y: "11.5", className: "middle" }),
            /* @__PURE__ */ jsx(RectStyled, { y: "16.5", className: "bottom" })
          ]
        }
      )
    }
  );
};
HamburgerButton.propTypes = {
  sx: PropTypes.object,
  isOpen: PropTypes.bool,
  onClick: PropTypes.func,
  testId: PropTypes.string
};
const StyledSkeleton = styled$1(Skeleton$1)(({ variant, sx }) => {
  const reverseAnimation = (sx == null ? void 0 : sx.reverseAnimation) ?? false;
  return {
    backgroundColor: "#d3d3d3",
    ...variant === SKELETON_VARIANTS.ROUNDED_RECT && {
      borderRadius: "100px"
    },
    ...variant === SKELETON_VARIANTS.ROUNDED && {
      borderRadius: "10px"
    },
    ...reverseAnimation && {
      opacity: 0.4,
      animation: "skeletonInvert 2s ease-in-out 0.5s infinite"
    },
    ...reverseAnimation && {
      "@keyframes skeletonInvert": {
        "0%": {
          opacity: 0.4
        },
        "50%": {
          opacity: 1
        },
        "100%": {
          opacity: 0.4
        }
      }
    }
  };
});
const Skeleton = ({ variant, ...props }) => {
  return /* @__PURE__ */ jsx(StyledSkeleton, { "data-testid": `${variant}-skeleton`, variant, ...props });
};
Skeleton.protoType = {
  variant: PropTypes.oneOf(Object.values(SKELETON_VARIANTS))
};
const getColors = (theme) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A;
  return {
    active: (_c = (_b = (_a = theme == null ? void 0 : theme.vars) == null ? void 0 : _a.palette) == null ? void 0 : _b.primary) == null ? void 0 : _c[110],
    default: (_f = (_e = (_d = theme == null ? void 0 : theme.vars) == null ? void 0 : _d.palette) == null ? void 0 : _e.grey) == null ? void 0 : _f[60],
    defaultBackground: (_i = (_h = (_g = theme == null ? void 0 : theme.vars) == null ? void 0 : _g.palette) == null ? void 0 : _h.grey) == null ? void 0 : _i[0],
    disabled: (_l = (_k = (_j = theme == null ? void 0 : theme.vars) == null ? void 0 : _j.palette) == null ? void 0 : _k.grey) == null ? void 0 : _l[30],
    error: (_o = (_n = (_m = theme == null ? void 0 : theme.vars) == null ? void 0 : _m.palette) == null ? void 0 : _n.secondary) == null ? void 0 : _o[90],
    filled: (_r = (_q = (_p = theme == null ? void 0 : theme.vars) == null ? void 0 : _p.palette) == null ? void 0 : _q.grey) == null ? void 0 : _r[70],
    label: {
      active: (_u = (_t = (_s = theme == null ? void 0 : theme.vars) == null ? void 0 : _s.palette) == null ? void 0 : _t.primary) == null ? void 0 : _u[100],
      error: (_x = (_w = (_v = theme == null ? void 0 : theme.vars) == null ? void 0 : _v.palette) == null ? void 0 : _w.secondary) == null ? void 0 : _x[90]
    },
    supportingText: {
      active: (_A = (_z = (_y = theme == null ? void 0 : theme.vars) == null ? void 0 : _y.palette) == null ? void 0 : _z.grey) == null ? void 0 : _A[50]
    }
  };
};
const StyledBox = styled(Box)(() => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  marginBottom: 20,
  width: "100%"
}));
const StyledAdornment = styled(({ hasPointer, isDisabled, ...rest }) => /* @__PURE__ */ jsx(InputAdornment, { ...rest }))(({ hasPointer, isDisabled }) => ({
  "&.MuiInputAdornment-root": {
    marginRight: 0,
    "svg, img": {
      flexShrink: 0,
      height: 24,
      width: 24
    },
    ...hasPointer ? { cursor: "pointer" } : {},
    ...isDisabled ? { opacity: 0.5 } : {}
  }
}));
const StyledLabel = styled(InputLabel)(({ theme, state = "default" }) => {
  var _a, _b;
  const colors = getColors(theme);
  return {
    color: (colors == null ? void 0 : colors[state]) ?? colors.default,
    fontSize: 12,
    fontStyle: "normal",
    fontFamily: (_a = theme == null ? void 0 : theme.typography) == null ? void 0 : _a.fontFamily,
    fontWeight: 600,
    lineHeight: (_b = theme == null ? void 0 : theme.customSpacing) == null ? void 0 : _b.lineHeight,
    margin: "0 0 2px 17px",
    "&.Mui-disabled": {
      color: colors.disabled
    },
    "&.Mui-error": {
      color: colors.label.error
    },
    "&.Mui-focused": {
      color: colors.label.active
    }
  };
});
const StyledSupportingText = styled(FormHelperText)(({ theme, state = "default" }) => {
  var _a, _b, _c;
  const colors = getColors(theme);
  return {
    color: (colors == null ? void 0 : colors[state]) ?? colors.default,
    fontSize: 12,
    fontStyle: "normal",
    fontFamily: (_a = theme == null ? void 0 : theme.typography) == null ? void 0 : _a.fontFamily,
    fontWeight: (_b = theme == null ? void 0 : theme.typography) == null ? void 0 : _b.fontWeightRegular,
    lineHeight: (_c = theme == null ? void 0 : theme.customSpacing) == null ? void 0 : _c.lineHeight,
    margin: "4px 0 0 17px",
    "&.Mui-disabled": {
      color: colors.disabled
    },
    "&.Mui-error": {
      color: colors.label.error
    },
    "&.Mui-focused": {
      color: colors.supportingText.active
    }
  };
});
const StyledInput = styled(
  ({ shouldReducePaddingRight = false, state = "default", theme, ...rest }) => /* @__PURE__ */ jsx(OutlinedInput, { ...rest })
)(({ theme, shouldReducePaddingRight, state }) => {
  var _a, _b, _c;
  const colors = getColors(theme);
  return {
    backgroundColor: colors.defaultBackground,
    borderRadius: 10,
    boxSizing: "border-box",
    color: (colors == null ? void 0 : colors[state]) ?? colors.default,
    flexShrink: 0,
    fontSize: 16,
    fontStyle: "normal",
    fontWeight: (_a = theme == null ? void 0 : theme.typography) == null ? void 0 : _a.fontWeightRegular,
    gap: 8,
    height: 50,
    lineHeight: (_b = theme == null ? void 0 : theme.customSpacing) == null ? void 0 : _b.lineHeight,
    padding: `0 ${shouldReducePaddingRight ? "12px" : "14px"} 0 14px`,
    maxWidth: 288,
    "& .MuiOutlinedInput-input": {
      boxSizing: "border-box",
      height: 50,
      padding: 0,
      width: 288,
      "&.Mui-disabled": {
        color: colors.disabled,
        WebkitTextFillColor: colors.disabled
      },
      "::placeholder": {
        fontFamily: (_c = theme == null ? void 0 : theme.typography) == null ? void 0 : _c.fontFamily,
        color: (colors == null ? void 0 : colors[state]) ?? colors.default,
        opacity: 1
      }
    },
    "&.Mui-disabled": {
      color: colors.disabled,
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          border: `1.5px solid ${colors.disabled}`
        }
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1.5px solid ${colors.disabled}`
      }
    },
    "&.Mui-error": {
      color: colors.filled,
      paddingRight: 14,
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1.5px solid ${colors.error}`
      },
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          border: `1.5px solid ${colors.error}`
        }
      }
    },
    "&.Mui-focused": {
      color: colors.filled,
      paddingRight: 14,
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1.5px solid ${colors.active}`
      },
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          border: `1.5px solid ${colors.active}`
        }
      }
    },
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1.5px solid ${(colors == null ? void 0 : colors[state]) ?? colors.default}`
      }
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1.5px solid ${(colors == null ? void 0 : colors[state]) ?? colors.default}`
    }
  };
});
const getAdornments = ({ clearAction, icon, iconAlt, isDisabled }) => {
  const adornment = {};
  if (icon) {
    const renderIcon = () => {
      switch (typeof icon) {
        case "function":
          return icon();
        case "object":
          const Icon = icon;
          return /* @__PURE__ */ jsx(Icon, {});
        case "string":
          return /* @__PURE__ */ jsx("img", { src: icon, alt: iconAlt ?? "start adornment" });
        default:
          throw new Error("Invalid icon type");
      }
    };
    adornment.startAdornment = /* @__PURE__ */ jsx(
      StyledAdornment,
      {
        "data-testid": "textField__startAdornment",
        isDisabled,
        position: "start",
        variant: "outlined",
        children: renderIcon()
      }
    );
  }
  if (clearAction) {
    adornment.endAdornment = /* @__PURE__ */ jsx(StyledAdornment, { hasPointer: true, position: "end", children: /* @__PURE__ */ jsx(
      Close,
      {
        tabIndex: 0,
        "aria-hidden": "false",
        "data-testid": clearAction.dataTestId ?? "textField__endAdornment",
        onClick: clearAction.onClick,
        onKeyDown: (event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            clearAction.onClick();
          }
        }
      }
    ) });
  }
  return adornment;
};
const TextField = ({
  autoComplete = "off",
  autoFocus = false,
  boxClassName = "",
  defaultValue = "",
  icon,
  iconAlt,
  id = "testId",
  inputClassName = "",
  isDisabled = false,
  label,
  labelClassName,
  onInputBlur = () => {
  },
  onInputChange = () => {
  },
  onInputFocused = () => {
  },
  placeholder,
  shouldBeShownAsError = false,
  supportingText = "",
  supportingTextClassName,
  sx = {},
  ...props
}) => {
  const inputRef = useRef(null);
  const [value, setValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(autoFocus);
  const [hasError, setHasError] = useState(shouldBeShownAsError || false);
  useEffect(() => {
    if (defaultValue !== value) {
      setValue(defaultValue);
    }
  }, [defaultValue]);
  useEffect(() => {
    setHasError(shouldBeShownAsError);
  }, [shouldBeShownAsError]);
  const handleChange = (event) => {
    var _a;
    const value2 = (_a = event.target) == null ? void 0 : _a.value;
    setValue(value2);
    onInputChange({ value: value2, inputRef });
  };
  const handleClear = () => {
    setValue("");
    onInputChange({ value: "", inputRef });
    setHasError(false);
    inputRef.current.focus();
  };
  const handleFocus = () => {
    setIsFocused(true);
    onInputFocused({ value, inputRef });
  };
  const handleBlur = () => {
    setIsFocused(false);
    onInputBlur({ value, inputRef });
  };
  const clearAction = !isDisabled && value ? { onClick: handleClear, dataTestId: `textField__clearButton--${id}` } : null;
  const inputState = (value == null ? void 0 : value.length) ? "filled" : "default";
  const shouldReducePaddingRight = (supportingText == null ? void 0 : supportingText.length) > 0 && (inputState !== "filled" || isDisabled);
  return /* @__PURE__ */ jsxs(StyledBox, { className: boxClassName, sx, children: [
    label && /* @__PURE__ */ jsx(
      StyledLabel,
      {
        className: labelClassName,
        "data-testid": `textField__label--${id}`,
        disabled: isDisabled,
        error: !isFocused && hasError,
        focused: isFocused,
        state: inputState,
        htmlFor: id,
        children: label
      }
    ),
    /* @__PURE__ */ jsx(
      StyledInput,
      {
        autoComplete,
        autoFocus,
        className: inputClassName,
        "data-testid": `textField__${id}`,
        disabled: isDisabled,
        error: hasError,
        id,
        inputMode: "text",
        inputRef,
        onBlur: handleBlur,
        onChange: handleChange,
        onFocus: handleFocus,
        placeholder,
        shouldReducePaddingRight,
        state: inputState,
        value,
        ...getAdornments({
          clearAction,
          icon,
          iconAlt,
          isDisabled
        }),
        ...props
      }
    ),
    supportingText && /* @__PURE__ */ jsx(
      StyledSupportingText,
      {
        className: supportingTextClassName,
        "data-testid": `textField__helperText--${id}`,
        disabled: isDisabled,
        error: hasError,
        focused: isFocused,
        state: inputState,
        children: supportingText
      }
    )
  ] });
};
TextField.propTypes = {
  autoFocus: PropTypes.bool,
  autoComplete: PropTypes.oneOf(["on", "off"]),
  /** Css/sass styles to be applied to the StyledBox */
  boxClassName: PropTypes.string,
  /** If exists, the value already stored for this input */
  defaultValue: PropTypes.string,
  /** The svg or the component to be rendered */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  /** Alt used for the given svg */
  iconAlt: PropTypes.string,
  /** Will be used to identify the input */
  id: PropTypes.string,
  /** Css/sass styles to be applied to the StyledInput */
  inputClassName: PropTypes.string,
  isDisabled: PropTypes.bool,
  label: PropTypes.string,
  /** Css/sass styles to be applied to the StyledLabel */
  labelClassName: PropTypes.string,
  /** The following functions will be invoked when the events are triggered passing { value, inputRef } */
  onInputBlur: PropTypes.func,
  onInputChange: PropTypes.func,
  onInputFocused: PropTypes.func,
  /** Input basic placeholder */
  placeholder: PropTypes.string,
  /** Indicates if the input should be in an error state */
  shouldBeShownAsError: PropTypes.bool,
  /** Text to be shown as help for users */
  supportingText: PropTypes.string,
  /** Css/sass styles to be applied to the StyledSupportingText */
  supportingTextClassName: PropTypes.string,
  sx: PropTypes.object
};
const SourceIdLinkStyled = styled$1(BYOButton)(
  ({ theme }) => {
    return {
      "&:focus-visible": {
        outline: "auto 1px black"
      }
    };
  }
);
const SourceIdLink = ({
  children,
  href,
  ...props
}) => {
  const { sourceid } = useSourceIdCookie();
  const url = `${href}?sourceid=${sourceid || `${SOURCE_PREFIX}nav-start`}`;
  return /* @__PURE__ */ jsx(
    SourceIdLinkStyled,
    {
      color: "blue",
      style: "filled",
      href: url,
      ...props,
      children
    }
  );
};
SourceIdLink.propTypes = {
  /** Content or React Node */
  children: PropTypes.node.isRequired,
  /** URL */
  href: PropTypes.string.isRequired
};
const ListStyled = styled(List)(({ theme }) => {
  var _a, _b;
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      color: (_a = theme.vars.palette.grey) == null ? void 0 : _a[80],
      display: "flex",
      alignItems: "flex-start",
      flexDirection: "column",
      padding: "0px",
      gap: "16px",
      li: {
        marginBottom: "0px"
      },
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.large)]: {
        borderRight: `1px solid ${(_b = theme.vars.palette.grey) == null ? void 0 : _b[10]}`,
        minWidth: "206px",
        maxWidth: "329px",
        paddingRight: "24px",
        width: "100%"
      },
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_extra_large)]: {
        paddingRight: "48px",
        maxWidth: "359px"
      }
    }
  };
});
const ListItemStyled = styled(ListItem)(({ theme }) => {
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      padding: "0"
    }
  };
});
const ListItemIconStyled = styled(ListItemIcon)(({ theme }) => {
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      minWidth: "0",
      paddingRight: "6px",
      alignSelf: "flex-start"
    }
  };
});
const CheckIconStyled = styled(CheckIcon)(({ theme }) => {
  var _a;
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      color: (_a = theme.vars.palette.success) == null ? void 0 : _a[90],
      width: "18px"
    }
  };
});
function ProviderHighlights({
  highlights,
  clientId
}) {
  return /* @__PURE__ */ jsx(ListStyled, { "data-testid": `highlights-list-${clientId}`, children: ((highlights == null ? void 0 : highlights.slice(0, 3)) ?? []).map((item, index) => /* @__PURE__ */ jsxs(
    ListItemStyled,
    {
      "data-testid": `highlight-item-${clientId}-${index}`,
      children: [
        /* @__PURE__ */ jsx(ListItemIconStyled, { children: /* @__PURE__ */ jsx(CheckIconStyled, {}) }),
        item
      ]
    },
    index
  )) });
}
ProviderHighlights.propTypes = {
  highlights: PropTypes.array.isRequired,
  clientId: PropTypes.string.isRequired
};
const LogoImage = styled("img")({
  [`#${MKT_CONTAINER_ID} &`]: {
    width: "100%",
    height: "100%",
    objectFit: "contain"
  }
});
function OfferLogo({
  sx = {},
  logoUrl,
  clientId,
  clientName
}) {
  return /* @__PURE__ */ jsx(
    LogoImage,
    {
      sx,
      src: logoUrl,
      alt: `${clientName} logo`,
      "data-testid": `offer-logo-${clientId}`,
      loading: "lazy",
      itemProp: "logo"
    }
  );
}
OfferLogo.propTypes = {
  sx: PropTypes.object,
  logoUrl: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired
};
const OfferCriteriaContainer = styled(Box)(({ theme }) => {
  var _a;
  return {
    [`#${MKT_CONTAINER_ID} & `]: {
      display: "flex",
      flexDirection: "row-reverse",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      height: "auto",
      minHeight: "26px",
      maxHeight: "52px",
      "& h3, & p": {
        margin: 0,
        color: (_a = theme.vars.palette.grey) == null ? void 0 : _a[80]
      },
      "& h3": {
        fontSize: "23px",
        fontWeight: 600,
        lineHeight: "115%"
      },
      "& p": {
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "160%",
        maxWidth: "329px"
      },
      minWidth: "85px",
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.large)]: {
        paddingLeft: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        height: "auto",
        gap: "4px",
        "& h3": {
          fontSize: "33px"
        },
        "& p": {
          maxWidth: "auto"
        },
        minWidth: "206px",
        width: "100%"
      },
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_extra_large)]: {
        paddingLeft: "48px",
        maxWidth: "359px"
      }
    }
  };
});
const OfferCriteria = ({
  sx = {},
  criteria,
  clientId
}) => {
  const creditScoreCriteria = criteria.find((item) => item.type === CRITERIA_TYPE);
  if (!creditScoreCriteria) {
    return null;
  }
  const { min, max, description } = creditScoreCriteria;
  const criteriaValue = min && max ? `${min} – ${max}` : min || max;
  return /* @__PURE__ */ jsxs(OfferCriteriaContainer, { sx, "data-testid": `offer-criteria-${clientId}`, children: [
    /* @__PURE__ */ jsx("h3", { className: "criteria-value", "data-testid": `criteria-value-${clientId}`, children: criteriaValue }),
    /* @__PURE__ */ jsx("p", { "data-testid": `criteria-description-${clientId}`, children: description })
  ] });
};
OfferCriteria.propTypes = {
  sx: PropTypes.object,
  criteria: PropTypes.arrayOf(PropTypes.object).isRequired,
  clientId: PropTypes.string.isRequired
};
const OfferCtaContainer = styled(Box)(({ theme }) => {
  var _a;
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      alignSelf: "stretch",
      height: "75px",
      width: "auto",
      gap: "8px",
      "small": {
        margin: 0,
        color: (_a = theme.vars.palette.grey) == null ? void 0 : _a[70],
        textAlign: "center",
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "160%",
        height: "22px"
      },
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.large)]: {
        width: "100%",
        minWidth: "190px",
        alignSelf: "auto",
        marginRight: "auto",
        paddingLeft: "24px",
        maxWidth: "fit-content"
      },
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_extra_large)]: {
        paddingLeft: "48px",
        marginRight: "unset"
      },
      a: {
        "&:active, &:visited": {
          color: theme.vars.palette.grey[0]
        }
      }
    }
  };
});
const CtaButton = styled(BYOButton)(({ theme }) => ({
  [`#${MKT_CONTAINER_ID} &`]: {
    width: "100%",
    height: "45px",
    padding: "13.5px 32px",
    fontSize: "18px",
    lineHeight: "115%"
  }
}));
const OfferCta = ({
  sx = {},
  clientId,
  clientName,
  ctaButtonText,
  ctaUrl,
  ctaAdditionalText = null,
  sourceId,
  marketingClickId,
  isTestMode = false
}) => {
  const ctaParams = {
    sourceid: sourceId,
    marketingclickid: marketingClickId
  };
  if (isTestMode) {
    ctaParams["test"] = true;
  }
  const modifiedCtaUrl = addQueryParams(ctaUrl, ctaParams);
  const additionalText = ctaAdditionalText || `${OFFER_CTA_PREFIX} ${clientName}`;
  return /* @__PURE__ */ jsxs(OfferCtaContainer, { sx, "data-testid": `cta-section-${clientId}`, children: [
    /* @__PURE__ */ jsx(
      CtaButton,
      {
        className: FOCUS_OUTLINE_CLASS,
        color: "green",
        style: "filled",
        iconRight: /* @__PURE__ */ jsx(OpenInNewIcon, {}),
        href: modifiedCtaUrl,
        target: "_blank",
        "data-testid": `cta-button-${clientId}`,
        children: ctaButtonText
      }
    ),
    /* @__PURE__ */ jsx("small", { "data-testid": `cta-subtext-${clientId}`, children: additionalText })
  ] });
};
OfferCta.propTypes = {
  sx: PropTypes.object,
  clientId: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  ctaButtonText: PropTypes.string.isRequired,
  ctaUrl: PropTypes.string.isRequired,
  ctaAdditionalText: PropTypes.string,
  sourceId: PropTypes.string.isRequired,
  marketingClickId: PropTypes.string.isRequired,
  isTestMode: PropTypes.bool
};
export {
  HamburgerButton as H,
  LinkResolver as L,
  NavigationLink as N,
  OfferCriteria as O,
  ProviderHighlights as P,
  SearchIcon as S,
  TextField as T,
  Logo as a,
  OfferCta as b,
  OfferLogo as c,
  Skeleton as d,
  SourceIdLink as e,
  PropTypes as f
};
//# sourceMappingURL=OfferCta-OU6Yjw7w.js.map
